import LogService from "../services/LogService";
import ICita from "../types/Cita";
import IEvent from "../types/Event";
import IHoliday from "../types/Holiday";
import ILog from "../types/Log";
import IPaciente from "../types/Paciente";
import * as constantes from "../common/Constantes";
import ConfigUtils from "./Config";
import Holiday from 'date-holidays';

const formatCitas = (data: ICita[], pacientes: IPaciente[]): IEvent[] => {
    try {
        const formattedCitas: IEvent[] = [];

        data.map(cita => {
            const paciente = pacientes.filter(x => x._id === cita.pacienteId)[0];

            if (!paciente) return null;

            let title;
            if (cita.primeraCita) title = `1º Visita - ${paciente.nombre} ${paciente.apellidos}`;
            else if (cita.asistencia === false)title = `${paciente.nombre} ${paciente.apellidos} - Ausente`;
            else title = `${paciente.nombre} ${paciente.apellidos}`;

            let formattedCita: IEvent = {
                id: cita._id,
                title,
                start: new Date(cita.start),
                end: new Date (cita.end),
                resource: { sala: cita.salaId, especialista: cita.especialistaId, repetirId: cita.repetirId, asistencia: cita.asistencia, confirmacion: cita.confirmacion?.sent, fechaConfirmacion: cita.confirmacion?.fecha },
                pacienteId: paciente._id
            };
            formattedCitas.push(formattedCita);
            return formattedCita;
        });
        return formattedCitas;

    } catch (e: any) {
        const error: ILog = { method: 'Utils.Agenda.formatCitas', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return [];
    }
};

const formatEvent = (event: IEvent): ICita | undefined => {
    try {
        const formattedEvent: ICita = {
            _id: event.id?.toString(),
            pacienteId: event.pacienteId ? event.pacienteId : '',
            especialistaId: event.resource.especialista,
            salaId: event.resource.sala,
            start: event.start,
            end: event.end,
            repetirId: event.resource.repetirId,
            pacienteNombre: event.title,
            motivo: event.motivo,
            valoracion: event.valoracion,
            razonamiento: event.razonamiento,
            tratamiento: event.tratamiento,
            userId: ConfigUtils.getUserId(),
            asistencia: event.resource.asistencia,
            confirmacion: { sent: event.resource.confirmacion, fecha: event.resource.fechaConfirmacion }
        };
        return formattedEvent;

    } catch (e: any) {
        const error: ILog = { method: 'Utils.Agenda.formatEvent', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const formatTimeEvent = (event: IEvent | undefined) => {
    try {
        if (event === undefined) return;

        var d = new Date(event.start),
        hoursStart = '' + (d.getHours()),
        minutesStart = '' + d.getMinutes();
    
        var d2 = new Date(event.end),
        hoursEnd = '' + (d2.getHours()),
        minutesEnd = '' + d2.getMinutes();
    
        if (minutesStart.length < 2)
            minutesStart = '0' + minutesStart;
    
        if (minutesEnd.length < 2)
            minutesEnd = '0' + minutesEnd;
    
        if (event.allDay) {
            return "Todo el día";  
        } else {
            return `de ${hoursStart}:${minutesStart} a ${hoursEnd}:${minutesEnd}`;
        }
    } catch (e: any) {
        const error: ILog = { method: 'Utils.Agenda.formatTimeEvent', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const formatDateEvent = (event: IEvent | undefined) => {
    try {
        if (event === undefined) return;

        var d = new Date(event.start),
        dayWeek = '' + d.getDay(),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();

        const dayWeekIndex = (parseInt(dayWeek) + 6) % 7;
    
        if (event.allDay) {
            return `${constantes.weekNames[dayWeekIndex]}, ${day} de ${constantes.monthNames[parseInt(month) - 1]} de ${year}`;  
        } else {
            return `${constantes.weekNames[dayWeekIndex]}, ${day} de ${constantes.monthNames[parseInt(month) - 1]} de ${year}`;
        }
    } catch (e: any) {
        const error: ILog = { method: 'Utils.Agenda.formatDateEvent', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const formatCitaDate = (cita: ICita) => {
    try {
        var d = new Date(cita.start),
        dayWeek = '' + d.getDay(),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();

        const dayWeekIndex = (parseInt(dayWeek) + 6) % 7;
    
        return `${constantes.weekNames[dayWeekIndex]}, ${day} de ${constantes.monthNames[parseInt(month) - 1]} de ${year}`;
    } catch (e: any) {
        const error: ILog = { method: 'Utils.Agenda.formatCitaDate', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const formatCitaTime = (cita: ICita) => {
    try {
        var d = new Date(cita.start),
        hoursStart = '' + (d.getHours()),
        minutesStart = '' + d.getMinutes();
    
        var d2 = new Date(cita.end),
        hoursEnd = '' + (d2.getHours()),
        minutesEnd = '' + d2.getMinutes();
    
        if (minutesStart.length < 2)
            minutesStart = '0' + minutesStart;
    
        if (minutesEnd.length < 2)
            minutesEnd = '0' + minutesEnd;
    
        return `de ${hoursStart}:${minutesStart} a ${hoursEnd}:${minutesEnd}`;
    } catch (e: any) {
        const error: ILog = { method: 'Utils.Agenda.formatCitaTime', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

// Muestra la fecha/hora de la cita en la hora LOCAL de la clínica, no la del dispositivo
// que la esté viendo/enviando — antes usaba Date.getHours()/getMinutes() (hora del
// navegador), lo que da una hora incorrecta si quien confirma la cita no está físicamente
// en el mismo huso horario que la clínica (p.ej. probando una clínica de Panamá desde España).
const formatCitaDateTime = (cita: ICita, pais?: string) => {
    try {
        const timeZone = pais ? getZonaEntidad(pais) : getZonaClinica();
        const d = new Date(cita.start);

        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
        }).formatToParts(d);

        const get = (type: string) => parts.find(p => p.type === type)?.value || '';
        const year = parseInt(get('year'));
        const month = parseInt(get('month'));
        const day = parseInt(get('day'));
        const hoursStart = get('hour');
        const minutesStart = get('minute');

        // Día de la semana a partir de la fecha ya resuelta en la zona horaria de la
        // clínica (getDay() solo lee el calendario, no depende de ninguna zona horaria).
        const dayWeekIndex = (new Date(year, month - 1, day).getDay() + 6) % 7;

        return `${constantes.weekNames[dayWeekIndex]} ${day} de ${constantes.monthNames[month - 1]} a las ${hoursStart}:${minutesStart}`;
    } catch (e: any) {
        const error: ILog = { method: 'Utils.Agenda.formatCitaDateTime', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

// Un festivo es una fecha completa, no un instante: date-holidays ancla start/end a la
// zona horaria del país del festivo (p.ej. America/Panama), lo que hace que un festivo
// "de un día" ocupe dos casillas del calendario si quien lo ve está en otra zona horaria
// (p.ej. España). Se normaliza a medianoche LOCAL (la del navegador) de la fecha que
// corresponde en la zona horaria del festivo, para que solo cuente el día, nunca la hora.
const normalizeHolidayDate = (date: Date, timeZone: string): Date => {
    const [year, month, day] = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' })
        .format(date)
        .split('-')
        .map(Number);
    return new Date(year, month - 1, day);
};

const getHolidays = (provincia: string, pais?: string, years?: number[]) => {
    try {
        const currentYear = new Date().getFullYear();
        const targetYears = years && years.length ? years : [currentYear, currentYear + 1];
        const paisFestivos = pais || ConfigUtils.getPais() || 'ES';
        const hd = provincia
            ? new Holiday(paisFestivos, provincia)
            : new Holiday(paisFestivos);
        const timezoneFestivos = hd.getTimezones()?.[0] || 'Europe/Madrid';
        const allHolidays = targetYears
            .flatMap(year => hd.getHolidays(year).filter(h => h.type === 'public'))
            .map(h => ({
                ...h,
                start: normalizeHolidayDate(h.start, timezoneFestivos),
                end: normalizeHolidayDate(h.end, timezoneFestivos),
            }));
        return allHolidays;
    } catch (e: any) {
        const error: ILog = { method: 'Utils.Agenda.getHolidays', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return [];
    }
};

// Una clínica que ya ha personalizado sus festivos (aunque sea de un solo año) deja de
// recibir los festivos por defecto de la librería para siempre: el fallback histórico solo
// se activaba con el array totalmente vacío. Esto rellena únicamente los años (actual y
// siguiente) que todavía no tengan ningún festivo guardado, sin tocar los ya personalizados.
const fillMissingYearsHolidays = (holidays: IHoliday[], provincia: string, pais?: string): IHoliday[] => {
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    const existingYears = new Set(holidays.map(h => new Date(h.start).getFullYear()));
    const missingYears = [currentYear, nextYear].filter(year => !existingYears.has(year));

    if (missingYears.length === 0) return holidays;

    const generated = getHolidays(provincia, pais, missingYears);
    return [...holidays, ...ConfigUtils.formatHolidays(generated)];
};

// España usa nuestra propia lista curada (constantes.provincias): los códigos coinciden
// exactamente con los de la librería, pero con nombres en español más cuidados que los suyos.
// Para el resto de países se consulta la librería en tiempo real — si no tiene datos
// regionales para ese país (la mayoría no los tiene), devuelve un array vacío y no se
// muestra ningún selector de provincia/estado.
const getProvincias = (pais: string): { id: string; name: string }[] => {
    if (pais === 'ES') return constantes.provincias;
    try {
        const hd = new Holiday();
        const states = hd.getStates(pais, 'es');
        if (!states) return [];
        return Object.entries(states).map(([id, name]) => ({ id, name }));
    } catch (e: any) {
        const error: ILog = { method: 'Utils.Agenda.getProvincias', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return [];
    }
};

// Zona horaria de un país y, si se conoce, de su provincia (la comunidad de date-holidays que guarda
// clinicProvincia): ES + CN (Canarias) → Atlantic/Canary; el resto de España, Europe/Madrid. Sin provincia, o con
// una que no es de ese país, la primera zona del país. Misma regla que la API (API/src/utils/fechas.ts -> getZona)
// — mantenerlas iguales.
const zonaPorPaisProvincia = new Map<string, string>();
export const getZona = (pais?: string | null, provincia?: string | null): string => {
    const paisZona = pais || 'ES';
    const clave = `${paisZona}|${provincia || ''}`;
    let zona = zonaPorPaisProvincia.get(clave);
    if (zona) return zona;
    try {
        const estados = provincia ? new Holiday().getStates(paisZona) : undefined;
        const provinciaValida = provincia && estados && Object.prototype.hasOwnProperty.call(estados, provincia) ? provincia : undefined;
        const hd = provinciaValida ? new Holiday(paisZona, provinciaValida) : new Holiday(paisZona);
        zona = hd.getTimezones()?.[0] || 'Europe/Madrid';
    } catch (e: any) {
        const error: ILog = { method: 'Utils.Agenda.getZona', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        zona = 'Europe/Madrid';
    }
    zonaPorPaisProvincia.set(clave, zona);
    return zona;
};

// Zona de la clínica con la que se ha iniciado sesión (para pasar a integraciones que la necesiten explícita, p.ej. el
// botón "Añadir al calendario", o para mostrar horas de la clínica).
export const getZonaClinica = (): string => getZona(ConfigUtils.getPais(), ConfigUtils.getProvincia());

// Zona de una entidad de facturación: la provincia de la clínica cuenta si la entidad es de su país; si es de otro
// (una entidad de Panamá), solo el suyo. Misma regla que la API (getZonaEntidad).
export const getZonaEntidad = (pais?: string | null): string => {
    const paisEntidad = pais || 'ES';
    const paisClinica = ConfigUtils.getPais() || 'ES';
    return getZona(paisEntidad, paisEntidad === paisClinica ? ConfigUtils.getProvincia() : undefined);
};

// Zona principal de un país, sin provincia.
export const getTimezone = (pais: string): string => getZona(pais);

// Diferencia en minutos entre la hora de una zona y UTC en un instante (+120 = UTC+2).
const offsetMinutos = (zona: string, fecha: Date): number => {
    const partes = new Intl.DateTimeFormat('en-US', {
        timeZone: zona, hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(fecha);
    const valor = (tipo: string) => Number(partes.find(p => p.type === tipo)?.value);
    const comoUtc = Date.UTC(valor('year'), valor('month') - 1, valor('day'), valor('hour'), valor('minute'), valor('second'));
    return Math.round((comoUtc - fecha.getTime()) / 60000);
};

// La agenda se pinta en la hora del navegador. Si ahora mismo no coincide con la de la clínica (alguien de viaje, o
// gestionando una clínica de otro país), se avisa.
export interface AvisoZona {
    clave: string;        // diferencia actual, para no repetir el aviso en la sesión salvo que cambie
    horaClinica: string;  // hora de ahora en la clínica
    horaLocal: string;    // hora de ahora en el dispositivo
    diferencia: string;   // "6 horas menos", "1 hora más", "4 h 30 min más"
    ejemploLocal: string; // las 10:00 de la clínica en el dispositivo
}

const textoDiferencia = (minutos: number): string => {
    const absoluto = Math.abs(minutos);
    const horas = Math.floor(absoluto / 60);
    const resto = absoluto % 60;
    const cantidad = horas && resto ? `${horas} h ${resto} min` : horas ? `${horas} ${horas === 1 ? 'hora' : 'horas'}` : `${resto} min`;
    return `${cantidad} ${minutos < 0 ? 'menos' : 'más'}`;
};

const horaCorta = (minutosDelDia: number): string =>
    `${Math.floor(minutosDelDia / 60)}:${String(minutosDelDia % 60).padStart(2, '0')}`;

export const avisoZonaNavegador = (ahora: Date = new Date()): AvisoZona | null => {
    try {
        const zonaClinica = getZonaClinica();
        const local = -ahora.getTimezoneOffset();
        const clinica = offsetMinutos(zonaClinica, ahora);
        if (local === clinica) return null;
        const diferencia = local - clinica;
        const hora = (timeZone?: string) => ahora.toLocaleTimeString('es-ES', { hour: 'numeric', minute: '2-digit', timeZone });
        // Las 10:00 de la clínica en el dispositivo, avisando si cae en otro día
        const ejemplo = 10 * 60 + diferencia;
        const dia = ejemplo < 0 ? ' del día anterior' : ejemplo >= 24 * 60 ? ' del día siguiente' : '';
        return {
            clave: `${local}|${clinica}`,
            horaClinica: hora(zonaClinica),
            horaLocal: hora(),
            diferencia: textoDiferencia(diferencia),
            ejemploLocal: `${horaCorta(((ejemplo % 1440) + 1440) % 1440)}${dia}`,
        };
    } catch (e: unknown) {
        const mensaje = (e as Error).message;
        const error: ILog = { method: 'Utils.Agenda.avisoZonaNavegador', level: 'error', message: mensaje, meta: mensaje };
        LogService.logError(error);
        return null;
    }
};

export const toDateOnly = (date: string | Date) => {
  const d = new Date(date);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

export const formatDateOnly = (date: Date) => {
  const d = toDateOnly(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const AgendaUtils = {
    formatCitas,
    formatTimeEvent,
    formatDateEvent,
    formatEvent,
    formatCitaDate,
    formatCitaTime,
    formatCitaDateTime,
    getHolidays,
    fillMissingYearsHolidays,
    getProvincias,
    getTimezone,
    getZona,
    getZonaClinica,
    getZonaEntidad,
    avisoZonaNavegador,
    toDateOnly,
    formatDateOnly
};

export default AgendaUtils;